// src/components/RouteErrorBoundary.tsx
import React from 'react';
import { AlertTriangle, RefreshCw, Home, Wifi } from 'lucide-react';
import { useTheme } from '../context/ThemeContext.tsx';

interface RouteErrorFallbackProps {
  error?: Error;
  resetError?: () => void;
  routeName?: string;
}

const RouteErrorFallback: React.FC<RouteErrorFallbackProps> = ({
  error,
  resetError,
  routeName = 'page',
}) => {
  const { darkMode } = useTheme();

  const handleRetry = () => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  // Check if it's a chunk loading error (common with lazy loading)
  const isChunkError =
    error?.message?.includes('Loading chunk') ?? error?.message?.includes('Loading CSS chunk');

  const isNetworkError = error?.message?.includes('fetch') ?? error?.message?.includes('network');

  const getErrorInfo = () => {
    if (isChunkError) {
      return {
        title: 'Loading Error',
        description: `There was an issue loading the ${routeName}. This might be due to a recent update.`,
        icon: <RefreshCw className="w-16 h-16 text-blue-500 mx-auto mb-4" />,
        suggestion: 'Try refreshing the page to get the latest version.',
      };
    }

    if (isNetworkError) {
      return {
        title: 'Connection Error',
        description: `Unable to load the ${routeName} due to a network issue.`,
        icon: <Wifi className="w-16 h-16 text-yellow-500 mx-auto mb-4" />,
        suggestion: 'Check your internet connection and try again.',
      };
    }

    return {
      title: 'Page Error',
      description: `The ${routeName} encountered an unexpected error.`,
      icon: <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />,
      suggestion: 'This error has been logged. You can try refreshing or return home.',
    };
  };

  const errorInfo = getErrorInfo();

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 ${
        darkMode ? 'bg-gray-900' : 'bg-orange-50'
      }`}
    >
      <div
        className={`max-w-md w-full rounded-xl shadow-lg p-6 text-center ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}
      >
        <div className="mb-6">
          {errorInfo.icon}
          <h1 className={`font-display text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {errorInfo.title}
          </h1>
          <p className={`mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {errorInfo.description}
          </p>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {errorInfo.suggestion}
          </p>
        </div>

        <div className="space-y-3 mb-6">
          <button
            onClick={handleRetry}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>{isChunkError ? 'Refresh Page' : 'Try Again'}</span>
          </button>

          <button
            onClick={handleGoHome}
            className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              darkMode
                ? 'bg-gray-600 hover:bg-gray-700 text-white'
                : 'bg-gray-500 hover:bg-gray-600 text-white'
            }`}
          >
            <Home className="w-4 h-4" />
            <span>Go Home</span>
          </button>
        </div>

        {process.env.NODE_ENV === 'development' && error && (
          <details className="text-left">
            <summary
              className={`flex items-center justify-center space-x-2 cursor-pointer text-sm hover:opacity-75 ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              <span>Development Details</span>
            </summary>
            <div
              className={`mt-3 p-3 rounded-lg text-xs ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
            >
              <pre
                className={`whitespace-pre-wrap ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}
              >
                {error.message}
              </pre>
            </div>
          </details>
        )}
      </div>
    </div>
  );
};

export default RouteErrorFallback;
